import {
  JSX,
 useState
} from 'react';
import {
 Plus
} from 'lucide-react';
import {
 Button
} from 'shared/components/ui/button';
import {
 ScrollArea
} from 'shared/components/ui/scroll-area';
import {
 Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import {
 DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from 'shared/components/ui/dropdown-menu';
import OpCodeConfigModal from './OpCodeConfigModal';
import ChatSection from './ChatSection';
import SectionList from './SectionList';
import {
 getOpCodeIcon, renderSectionPreview
} from './utils';
import {
 OpCode
} from '../types/storyBuilder.types';

interface Section {
  id: string;
  opCodeId: string;
  config: Record<string, any>;
}

interface PageBuilderProps {
  availableOpCodes: OpCode[];
  initialSections?: Section[];
  onSave: (sections: Section[]) => void;
}

export default function PageBuilder({
  availableOpCodes, initialSections = [], onSave
}: PageBuilderProps): JSX.Element {
  const [sections, setSections] = useState<Section[]>(initialSections);
  const [configuring, setConfiguring] = useState<Section | null>(null);

  // Add a new section to the page
  const addSection = (opCodeId: string) => {
    const newSection: Section = {
      id: `section-${Date.now()}`,
      opCodeId,
      config: {},
    };
    const updatedSections = [...sections, newSection];
    setSections(updatedSections);
    onSave(updatedSections);
  };

  // Remove a section from the page
  const removeSection = (sectionId: string) => {
    const updatedSections = sections.filter((section) => section.id !== sectionId);
    setSections(updatedSections);
    onSave(updatedSections);
  };

  // Get the OpCode by its id
  const getOpCodeById = (id: string): OpCode | undefined => {
    return availableOpCodes.find((opCode) => opCode.id === id);
  };

  // Handle section order changes
  const handleSectionOrderChange = (newOrder: Section[]) => {
    setSections(newOrder);
    onSave(newOrder);
  };

  return (
      <div className="flex flex-col w-full">
          <div className="border-b bg-background">
              <div className="container py-4">
                  <div className="flex items-center justify-between">
                      <div>
                          <h2 className="text-2xl font-semibold">Page Builder</h2>
                          <p className="text-sm text-muted-foreground">Design your page by adding and arranging sections</p>
                      </div>
                      <Tabs defaultValue="editor">
                          <TabsList>
                              <TabsTrigger value="editor">Editor</TabsTrigger>
                              <TabsTrigger value="preview">Preview</TabsTrigger>
                          </TabsList>
                      </Tabs>
                  </div>
              </div>
          </div>

          <div className="flex-1 flex">
              <ChatSection />
              <div className="flex-1 flex flex-col h-full">
                  <Tabs defaultValue="editor" className="flex-1">
                      <TabsContent value="editor" className="flex-1 border-0 m-0 p-6">
                          <ScrollArea className="">
                              <SectionList
                  sections={sections}
                  getOpCodeById={getOpCodeById}
                  onOrderChange={handleSectionOrderChange}
                  onRemove={removeSection}
                  onConfigure={setConfiguring}
                />
                          </ScrollArea>
                          <div className="mt-6">
                              <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                      <Button className="w-full">
                                          <Plus className="h-4 w-4 mr-2" />
                                          Add New Section
                                      </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                      {availableOpCodes.map((opCode) => (
                                          <DropdownMenuItem key={opCode.id} onClick={() => addSection(opCode.id)}>
                                              <div className="flex items-center">
                                                  {getOpCodeIcon(opCode.type)}
                                                  <span className="ml-2">{opCode.name}</span>
                                              </div>
                                          </DropdownMenuItem>
                    ))}
                                  </DropdownMenuContent>
                              </DropdownMenu>
                          </div>
                      </TabsContent>

                      <TabsContent value="preview" className="flex-1 border-0 m-0">
                          <ScrollArea className="h-[calc(100vh-12rem)]">
                              <div className="p-6 space-y-6">
                                  {sections.map((section) => {
                    const opCode = getOpCodeById(section.opCodeId);
                    if (!opCode) return null;
                    return (
                        <div key={section.id} className="mb-8">
                            {renderSectionPreview(opCode)}
                        </div>
                    );
                  })}
                              </div>
                          </ScrollArea>
                      </TabsContent>
                  </Tabs>
              </div>
          </div>

          {configuring && (
          <OpCodeConfigModal
          isOpen={true}
          onClose={() => setConfiguring(null)}
          opCode={getOpCodeById(configuring.opCodeId)!}
          sectionConfig={configuring.config}
          onSave={(newConfig) => {
            const updatedSections = sections.map((section) =>
              section.id === configuring.id ? {
                ...section,
                config: newConfig
              } : section,
            );
            setSections(updatedSections);
            onSave(updatedSections);
            setConfiguring(null);
          }}
        />
      )}
      </div>
  );
}
